import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { findIana } from "windows-iana";

import { Holiday, HolidayOccurrence } from "../types";

// Date keys are "yyyy-MM-dd" strings. A holiday is a date at the Location,
// not an instant, so all arithmetic here is done on keys (via UTC) to stay
// independent of the browser's time zone.

const DAY_MS = 24 * 60 * 60 * 1000;

export const DAY_NAMES = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
];

export const getBrowserTimeZone = (): string =>
	Intl.DateTimeFormat().resolvedOptions().timeZone;

// Locations carry Windows zone names ("Eastern Standard Time"); some may
// already be IANA ("America/New_York").
export const getIanaTimeZone = (timeZone?: string | null): string => {
	if (!timeZone) return getBrowserTimeZone();
	if (timeZone.includes("/")) return timeZone;
	try {
		const ianaZones = findIana(timeZone);
		return ianaZones[0] ?? getBrowserTimeZone();
	} catch (err) {
		return getBrowserTimeZone();
	}
};

export const toDateKey = (value: string): string => value.slice(0, 10);

// The calendar date an instant falls on at the given IANA zone
export const getDateKeyInTimeZone = (instant: Date, timeZone: string): string => {
	try {
		return formatInTimeZone(instant, timeZone, "yyyy-MM-dd");
	} catch (err) {
		return formatInTimeZone(instant, getBrowserTimeZone(), "yyyy-MM-dd");
	}
};

// The calendar date a browser-local Date sits on (e.g. a scheduler column)
export const getLocalDateKey = (date: Date): string => {
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${date.getFullYear()}-${month}-${day}`;
};

const keyToUtc = (dateKey: string): Date => new Date(`${dateKey}T00:00:00Z`);

const utcToKey = (date: Date): string => date.toISOString().slice(0, 10);

export const addDaysToKey = (dateKey: string, days: number): string =>
	utcToKey(new Date(keyToUtc(dateKey).getTime() + days * DAY_MS));

export const getDayOfWeek = (dateKey: string): number =>
	keyToUtc(dateKey).getUTCDay();

// Parses a date key as local midnight, for display with date-fns format()
export const dateKeyToLocalDate = (dateKey: string): Date => {
	const [year, month, day] = dateKey.split("-").map(Number);
	return new Date(year, month - 1, day);
};

const isLeapYear = (year: number): boolean =>
	(year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

// The date a holiday is observed on in a given year, or null if it isn't
// observed that year. Recurring Feb 29 is observed on Feb 28 in non-leap years.
export const getObservedDateKey = (
	holiday: Holiday,
	year: number
): string | null => {
	const holidayKey = toDateKey(holiday.date);
	const holidayYear = Number(holidayKey.slice(0, 4));
	if (!holiday.isRecurringYearly) {
		return holidayYear === year ? holidayKey : null;
	}
	if (year < holidayYear) return null;
	let monthDay = holidayKey.slice(5);
	if (monthDay === "02-29" && !isLeapYear(year)) monthDay = "02-28";
	return `${year}-${monthDay}`;
};

// Every holiday occurrence within [fromKey, toKey], ascending by date
export const getHolidayOccurrences = (
	holidays: Holiday[],
	fromKey: string,
	toKey: string
): HolidayOccurrence[] => {
	const fromYear = Number(fromKey.slice(0, 4));
	const toYear = Number(toKey.slice(0, 4));
	const occurrences: HolidayOccurrence[] = [];
	holidays.forEach((holiday) => {
		if (holiday.isActive === false) return;
		for (let year = fromYear; year <= toYear; year++) {
			const dateKey = getObservedDateKey(holiday, year);
			if (dateKey && dateKey >= fromKey && dateKey <= toKey) {
				occurrences.push({ dateKey, holiday });
			}
		}
	});
	return occurrences.sort((a, b) => a.dateKey.localeCompare(b.dateKey));
};

export const getHolidaysOnDate = (
	holidays: Holiday[],
	dateKey: string
): Holiday[] =>
	getHolidayOccurrences(holidays, dateKey, dateKey).map(
		(occurrence) => occurrence.holiday
	);

// Hard cap so a runaway "On Date" far in the future can't hang the UI
const MAX_REPEAT_OCCURRENCES = 1000;

// The dates a weekly repeat pattern lands on, starting at startKey (inclusive)
export const getRepeatOccurrenceDateKeys = ({
	startKey,
	daysOfWeek,
	endKey,
	occurrences,
}: {
	startKey: string;
	daysOfWeek: number[];
	endKey?: string | null;
	occurrences?: number | null;
}): string[] => {
	if (daysOfWeek.length === 0) return [];
	const limit = Math.min(
		occurrences && occurrences > 0 ? occurrences : MAX_REPEAT_OCCURRENCES,
		MAX_REPEAT_OCCURRENCES
	);
	if (!endKey && !occurrences) return [];
	const dateKeys: string[] = [];
	let cursor = startKey;
	while (dateKeys.length < limit) {
		if (endKey && cursor > endKey) break;
		if (daysOfWeek.includes(getDayOfWeek(cursor))) dateKeys.push(cursor);
		cursor = addDaysToKey(cursor, 1);
	}
	return dateKeys;
};

// "Eastern Time" style label for a zone, in the UI language
export const getTimeZoneLabel = (
	timeZone: string,
	at: Date,
	language = "en"
): string => {
	try {
		const parts = new Intl.DateTimeFormat(language, {
			timeZone,
			timeZoneName: "longGeneric" as Intl.DateTimeFormatOptions["timeZoneName"],
		}).formatToParts(at);
		return parts.find((part) => part.type === "timeZoneName")?.value ?? timeZone;
	} catch (err) {
		return timeZone;
	}
};

// Whether two zones show a different wall-clock time at the given instant
export const isDifferentWallClock = (
	timeZoneA: string,
	timeZoneB: string,
	at: Date
): boolean => {
	try {
		return (
			formatInTimeZone(at, timeZoneA, "yyyy-MM-dd HH:mm") !==
			formatInTimeZone(at, timeZoneB, "yyyy-MM-dd HH:mm")
		);
	} catch (err) {
		return false;
	}
};

// English locales whose short zone names cover the zones Locations use:
// en-US has the US/Canada abbreviations (EDT, PST, AKST, HST…) but falls back
// to "GMT+9:30" elsewhere, so the others fill in IST, ACST, BST, CEST, NZST…
const ZONE_NAME_LOCALES = ["en-US", "en-CA", "en-GB", "en-AU", "en-IN", "en-NZ"];

// Short name of a zone at an instant ("EDT" in summer, "EST" in winter), or
// its GMT offset ("GMT+9") where no common abbreviation exists
export const getTimeZoneAbbreviation = (timeZone: string, at: Date): string => {
	let fallback = "";
	for (const locale of ZONE_NAME_LOCALES) {
		try {
			const name = new Intl.DateTimeFormat(locale, {
				timeZone,
				timeZoneName: "short",
			})
				.formatToParts(at)
				.find((part) => part.type === "timeZoneName")?.value;
			if (!name) continue;
			// "GMT"/"UTC" alone is a real name (London in winter); "GMT+9" isn't
			if (!/^(GMT|UTC)[+-]/.test(name)) return name;
			fallback ||= name;
		} catch (err) {
			// Unknown zone for this runtime; try the next locale
		}
	}
	return fallback || timeZone;
};

// The Location's zone abbreviation ("EDT") on a holiday date, or null when
// the viewer's own zone shows the same wall-clock time there (no need to say it)
export const getOtherZoneAbbreviation = (
	dateKey: string,
	timeZone: string,
	viewerTimeZone: string = getBrowserTimeZone()
): string | null => {
	try {
		// Midday avoids landing inside a DST transition
		const instant = zonedTimeToUtc(`${dateKey}T12:00:00`, timeZone);
		if (!isDifferentWallClock(timeZone, viewerTimeZone, instant)) return null;
		return getTimeZoneAbbreviation(timeZone, instant);
	} catch (err) {
		return null;
	}
};
